/**
 * Editor для Прийняття чужої сировини на переробку (Порція 4b).
 * Patterns як у ProductionServiceActEditor.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import SearchSelect from '@/components/SearchSelect';
import {
  materialReceiptsForProcessing,
  type MaterialReceiptItemIn,
} from '@/api/materialReceiptsForProcessing';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.material_receipt.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  destination_warehouse_id: string;
  comment: string;
  project_id: string | null;
  items: MaterialReceiptItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  destination_warehouse_id: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number): MaterialReceiptItemIn => ({
  line_number: n,
  nomenclature_id: '',
  quantity: '0',
  declared_value: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function MaterialReceiptForProcessingEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });
  const [tab, setTab] = useState<'items' | 'accounts'>('items');
  // ч.687: підбір/швидке створення номенклатури (канон processing-receipts).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickNomOpen, setQuickNomOpen] = useState(false);

  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.customer_contractor_id],
    queryFn: () => refsApi.contracts({
      organization_id: form.organization_id,
      contractor_id: form.customer_contractor_id,
    }),
    enabled: !!form.organization_id && !!form.customer_contractor_id,
  });

  const docQuery = useQuery({
    queryKey: ['material-receipt-for-processing', id],
    queryFn: () => materialReceiptsForProcessing.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  const numberPreview = useNumberPreview(
    'doc.material_receipt_for_processing',
    form.organization_id,
    isNew,
  );

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        destination_warehouse_id: d.destination_warehouse_id ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          declared_value: it.declared_value ?? '0',
          account_code: it.account_code ?? null,
          // ч.687: legacy-док без складу рядка — показуємо склад шапки.
          warehouse_id: it.warehouse_id ?? d.destination_warehouse_id ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  const totalDeclared = useMemo(
    () =>
      form.items.reduce((acc, it) => acc + (Number(it.declared_value) || 0), 0),
    [form.items],
  );

  const create = useMutation({
    mutationFn: () =>
      materialReceiptsForProcessing.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
      navigate(`/material-receipts-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      materialReceiptsForProcessing.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await materialReceiptsForProcessing.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      try {
        await materialReceiptsForProcessing.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/material-receipts-for-processing/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
      navigate(`/material-receipts-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const updateAndPost = useMutation({
    mutationFn: async () => {
      await materialReceiptsForProcessing.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await materialReceiptsForProcessing.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => materialReceiptsForProcessing.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => materialReceiptsForProcessing.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
      navigate('/material-receipts-for-processing');
    },
    onError: (e) => setError(extractError(e)),
  });

  // ч.687: рядки — попапом мультивибору / швидким створенням.
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((row, k) => ({
        ...newRow(f.items.length + 1 + k),
        nomenclature_id: row.nomenclature_id,
        quantity: '1',
      }))],
    }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items
        .filter((_, idx) => idx !== i)
        .map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<MaterialReceiptItemIn>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  const nomName = (nid: string) => {
    const n = nomQ.data?.items.find((x) => x.id === nid);
    return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—';
  };

  // ч.911 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'customer', title: 'Замовник', required: 1,
      filled: form.customer_contractor_id ? 1 : 0 },
    { id: 'items', title: 'Чужа сировина', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове прийняття чужої сировини' : `Прийняття: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.material_receipt_for_processing', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/material-receipts-for-processing')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          savePending={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/material-receipts-for-processing/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.material_receipt_for_processing', id: isNew ? '' : id! }} />
      }>
      {/* ч.687 (розкладка власника): [Юрособа|Номер|Дата] →
          [Замовник|Договір(+створити)]. Склад — у РЯДКАХ ТЧ. ч.911 — блоками. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.code}
              </option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Замовник">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Замовник (власник сировини) *</label>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.customer_contractor_id}
            onChange={(v) => setForm({ ...form, customer_contractor_id: v, contract_id: '' })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            Договір{' '}
            <a href={`/refs/contracts/new${form.customer_contractor_id ? `?contractor_id=${form.customer_contractor_id}` : ''}`}
               target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', fontWeight: 'normal',
               color: 'var(--primary)', marginLeft: 6 }}>створити ↗</a>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.customer_contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
      </div>
      </DocBlock>

      {/* ч.911: кнопки ТЧ — у шапці блоку (лише для непроведеного). */}
      <DocBlock index={3} title="Чужа сировина"
        actions={!isPosted ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="primary" onClick={() => setPickerOpen(true)}
              disabled={!form.organization_id}
              title={!form.organization_id ? 'Спершу оберіть юрособу'
                : 'Підбір з довідника номенклатури (мультивибір)'}>
              + додати номенклатуру
            </button>
            <button type="button" className="btn-doc" onClick={() => setQuickNomOpen(true)}
              title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
              створити номенклатуру
            </button>
          </div>
        ) : undefined}>
      {/* Таблична частина — вкладки (канон seg-tabs, «Рахунки обліку» справа) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'items' ? 'active' : ''}
            onClick={() => setTab('items')}>Чужа сировина ({form.items.length})</button>
        </div>
        {tab === 'items' && (
          <span style={{ fontWeight: 600 }}>
            Задекларована вартість: {totalDeclared.toFixed(2)}
          </span>
        )}
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.nomenclature_id).map((it) => {
                const i = form.items.indexOf(it);
                return (
                  <tr key={i}>
                    <td>{nomName(it.nomenclature_id)}</td>
                    <td>{acctSelect(it.account_code, (v) => setItemField(i, { account_code: v }), '022')}</td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — рахунок за замовчуванням (вказаний у дужках). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {tab === 'items' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Сировина</th>
                <th style={{ width: 170 }}>Склад призначення *</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 150 }}
                    title="Оцінка замовника — база матвідповідальності; позабаланс 022 ведеться за нею">
                  Декл. вартість *</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.line_number}</td>
                  <td>
                    <NomenclatureSelect
                      value={it.nomenclature_id}
                      disabled={isPosted}
                      onChange={(v) => setItemField(i, { nomenclature_id: v })}
                      kind="goods"
                    />
                  </td>
                  <td>
                    <select value={it.warehouse_id ?? ''}
                            onChange={(e) => setItemField(i, { warehouse_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">— оберіть склад —</option>
                      {warehousesQ.data?.map((w) => (
                        <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.001"
                      value={it.quantity}
                      onChange={(e) => setItemField(i, { quantity: e.target.value })}
                      disabled={isPosted}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={it.declared_value ?? '0'}
                      onChange={(e) => setItemField(i, { declared_value: e.target.value })}
                      disabled={isPosted}
                    />
                  </td>
                  <td>
                    {!isPosted && (
                      <button onClick={() => removeItem(i)} title="Видалити">
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {/* ч.687: попап підбору (мультивибір) + швидке створення. */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            addRowsFromPicker([{
              warehouse_id: '', warehouse_code: '', warehouse_name: '',
              nomenclature_id: nom.id, nomenclature_code: nom.code,
              nomenclature_article: null, nomenclature_name: nom.name,
              quantity: '0', cost: null,
            } as GoodsBalanceRow]);
          }}
        />
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.material_receipt_for_processing" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && (
        <div className="error" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
